import { Controller, FormProvider, useForm } from "react-hook-form";
import { useEffect } from "react";

import FlashContext from "components/FlashMessages/FlashContext";
import ComboBox from "ds/components/ComboBox";
import ComboBoxDefaultItem from "ds/components/ComboBox/DefaultItem";
import FormFooterEdit from "components/Forms/FooterEdit";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import { makeScanUpdateInput } from "shared/Scan/helpers";
import tooltips from "shared/Scan/tooltips";
import useScanFormOptions from "shared/Scan/useScanFormOptions";
import useScanUpdate from "shared/Scan/useScanUpdate";
import IntegrationField from "views/Account/NewScanSchedule/Steps/AttachProviders/IntegrationField";
import RegionsField from "views/Account/NewScanSchedule/Steps/AttachProviders/RegionsField";

import { ScanSettingsContextData } from "../Context";
import { REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES } from "../constants";
import { getFormDefaultValues, ScanSettingsCloudProviderFormFields } from "./helpers";

const ScanSettingsCloudProviderEdit = () => {
  const { scanSettings } = useTypedContext(ScanSettingsContextData);
  const { reportSuccess } = useTypedContext(FlashContext);

  const [updateScan, { loading }] = useScanUpdate({
    refetchQueries: REFETCH_SCAN_AND_SCAN_SETTINGS_QUERIES,
  });

  const {
    providerVersions,
    regions,
    integrations,
    loading: formOptionsLoading,
    error: formOptionsError,
  } = useScanFormOptions(scanSettings.space.id);

  const form = useForm<ScanSettingsCloudProviderFormFields>({
    defaultValues: getFormDefaultValues(scanSettings),
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    formState: { isDirty, errors },
    reset,
    setValue,
    watch,
  } = form;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const onSubmit = (formData: ScanSettingsCloudProviderFormFields) => {
    updateScan(
      { id: scanSettings.id, input: makeScanUpdateInput(scanSettings, formData) },
      (data) => {
        if (data?.scanUpdate) {
          reportSuccess({ message: "Scan was successfully updated" });
        }
      }
    );
  };

  useEffect(() => {
    reset(getFormDefaultValues(scanSettings));
  }, [reset, scanSettings]);

  const selectedProviderVersion = watch("providerVersion");
  const selectedIntegrationId = watch("integrationId");
  const selectedRegions = watch("regions");

  useEffect(() => {
    if (formOptionsLoading || formOptionsError) {
      return;
    }

    if (selectedProviderVersion && !providerVersions.includes(selectedProviderVersion)) {
      setValue("providerVersion", "");
    }

    if (
      selectedIntegrationId &&
      !integrations.some((integration) => integration.id === selectedIntegrationId)
    ) {
      setValue("integrationId", "");
    }

    const validRegions = selectedRegions.filter((regionId) =>
      regions.some((region) => region.id === regionId)
    );

    if (validRegions.length !== selectedRegions.length) {
      setValue("regions", validRegions);
    }
  }, [
    formOptionsLoading,
    formOptionsError,
    providerVersions,
    integrations,
    regions,
    selectedProviderVersion,
    selectedIntegrationId,
    selectedRegions,
    setValue,
  ]);

  const providerVersionOptions = providerVersions.map((version) => ({
    value: version,
    label: version,
  }));

  const providerVersionHelperText = formOptionsError
    ? "Could not load provider versions."
    : undefined;
  const regionsHelperText = formOptionsError ? "Could not load regions." : undefined;
  const integrationHelperText = formOptionsError ? "Could not load integrations." : undefined;

  return (
    <FormProvider {...form}>
      <Controller
        name="providerVersion"
        control={control}
        rules={{ required: "Provider version is required." }}
        render={({ field }) => (
          <ComboBox
            label="Provider version"
            tooltipInfo={tooltips.PROVIDER_VERSION}
            error={errors?.providerVersion?.message}
            helperText={providerVersionHelperText}
            value={field.value}
            items={providerVersionOptions}
            isLoading={formOptionsLoading}
            onChange={(value) => field.onChange(value ?? "")}
            placeholder="Select a provider version"
          >
            {(item) => <ComboBoxDefaultItem id={item.value} {...item} />}
          </ComboBox>
        )}
      />

      <Controller
        name="integrationId"
        control={control}
        rules={{ required: "Integration is required." }}
        render={({ field }) => (
          <IntegrationField
            value={field.value}
            onChange={field.onChange}
            options={integrations}
            isLoading={formOptionsLoading}
            error={errors?.integrationId?.message}
            helperText={integrationHelperText}
            noMargin
          />
        )}
      />

      <Controller
        name="regions"
        control={control}
        rules={{
          validate: (value) => value.length > 0 || "Select at least one region.",
        }}
        render={({ field }) => (
          <RegionsField
            value={field.value}
            onChange={field.onChange}
            options={regions}
            isLoading={formOptionsLoading}
            error={errors?.regions?.message}
            helperText={regionsHelperText}
            noMargin
          />
        )}
      />

      <FormFooterEdit
        onSubmit={handleSubmit(onSubmit)}
        onCancel={() => reset(getFormDefaultValues(scanSettings))}
        isDirty={isDirty}
        submitLoading={loading}
      />
    </FormProvider>
  );
};

export default ScanSettingsCloudProviderEdit;
